import { useTranslation } from 'react-i18next';
import {
  NETWORK_ERROR_STATUS,
  getRecognitionErrorCorrelationId,
  getRecognitionErrorMessage,
  getRecognitionErrorStatus,
  useEuropeUsaImport,
} from '@/api/recognition';
import type { EuropeUsaImportResultDto } from '@/api/types';

interface Props {
  csv: string;
  ou: string;
  preview: EuropeUsaImportResultDto;
  importer: ReturnType<typeof useEuropeUsaImport>;
  onBack: () => void;
  onDone: () => void;
}

/** Paso 4 del wizard de Usuarios: recapitulación + confirmación EXPLÍCITA (nunca automática). */
export function StepConfirm({ csv, ou, preview, importer, onBack, onDone }: Readonly<Props>) {
  const { t } = useTranslation('recognition');

  const committed = importer.data && !importer.data.preview ? importer.data : null;

  const confirm = () => importer.mutate({ csv, preview: false, ou });

  return (
    <div className="bulk-step bulk-step--confirm">
      {!committed && (
        <>
          <p className="bulk-intro">
            {t('admin.bulkUsers.confirmRecap', {
              total: preview.totalRows, created: preview.created,
              updated: preview.updated, deactivated: preview.deactivated,
            })}
          </p>

          {importer.isError && (
            <p className="rp-error" role="alert">
              <i className="fas fa-triangle-exclamation" />{' '}
              {getRecognitionErrorStatus(importer.error) === NETWORK_ERROR_STATUS
                // Nunca hubo respuesta HTTP: el backend puede haber terminado de escribir igual
                // (bug real 2026-09-04, ver client.ts NETWORK_ERROR_STATUS). El correlationId permite
                // cruzar contra el ImportAudit persistido en Mongo aunque los logs ya no existan.
                ? t('admin.bulkUsers.importErrorTimeout', {
                    correlationId: getRecognitionErrorCorrelationId(importer.error) ?? '—',
                  })
                : getRecognitionErrorMessage(importer.error, t('admin.bulkUsers.importError'))}
            </p>
          )}

          <div className="bulk-wizard-nav">
            <button type="button" className="btn btn--secondary" onClick={onBack} disabled={importer.isPending}>
              <i className="fas fa-arrow-left" /> {t('bulk.back')}
            </button>
            <button type="button" className="btn btn--send" onClick={confirm} disabled={importer.isPending}>
              <i className="fas fa-check" /> {t('admin.bulkUsers.confirm')}
            </button>
          </div>
        </>
      )}

      {committed && (
        <>
          <p className="adm-note adm-note--ok adm-note--strong">
            <i className="fas fa-circle-check" />{' '}
            {t('admin.bulkUsers.committed', {
              created: committed.created, updated: committed.updated,
              deactivated: committed.deactivated, skipped: committed.skipped,
            })}
          </p>
          <div className="bulk-wizard-nav">
            <span />
            <button type="button" className="btn btn--send" onClick={onDone}>{t('common.close')}</button>
          </div>
        </>
      )}
    </div>
  );
}

export default StepConfirm;
